import React, { useEffect, useState } from "react";
import Sheet from "@amzn/meridian/sheet";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Icon from "@amzn/meridian/icon";
import downloadSmallTokens from "@amzn/meridian-tokens/base/icon/download-small";
import { Space } from "../../space/Space";
import { HorizontalLine } from "../../horizontal-line/HorizontalLine";
import { FileUploadModal } from "./FileUploadModal";
import _ from "lodash";
import { Storage } from "aws-amplify";

type Attachment = {
    fileName: string;
    canopyS3Key: string;
};

export const AttachmentsSheet: React.FC<{
    data: any;
    open: boolean;
    onClose: () => void;
    handleInputChange: any;
    attachmentYear: number | string;
    allowUpload: boolean;
    year: number | string;
    updateData: any;
}> = ({
    data,
    open,
    onClose,
    handleInputChange,
    attachmentYear,
    allowUpload,
    year,
    updateData,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    const handleAddAttachment = () => {
        setIsModalOpen(true);
    };

    const handleFileUpload = (file: File, s3Key: string) => {
        setAttachments([...attachments, { fileName: file.name, canopyS3Key: s3Key }]);
        handleInputChange("appealDetails.appealApproachCalculationData.summary.attachments", [
            ...attachments,
            { fileName: file.name, canopyS3Key: s3Key },
        ]);
        updateData(data.appealDetails);
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (year.toString() === attachmentYear.toString()) {
            setAttachments(
                _.get(data, "appealDetails.appealApproachCalculationData.summary.attachments", [])
            );
        } else {
            setAttachments(
                _.get(
                    data,
                    `appealDetails.appealApproachCalculationData.summary.previousAppeals.${attachmentYear}.attachments`,
                    []
                )
            );
        }
    }, [year, attachmentYear]);

    const handleDownload = async (attachment: Attachment) => {
        try {
            const signedURL = await Storage.get(attachment.canopyS3Key, { expires: 60 }); // URL expires in 60 seconds
            window.open(signedURL, "_blank");
        } catch (error) {
            console.error("Error generating signed URL:", error);
        }
    };

    return (
        <>
            <Sheet type="overlay" side="end" open={open} onClose={onClose} spacingInset={"500"}>
                <Space size={"m"} />
                <Row width="100%" widths={["fill", "fit"]}>
                    <Column spacing="300" height={250} width={"100%"}>
                        <Row width={"100%"} widths={["fill", "fit"]}>
                            <Text type="h300">{`Attachments ${attachmentYear}`}</Text>
                            <Button type={"icon"} onClick={onClose}>
                                <Icon tokens={closeSmallTokens} />
                            </Button>
                        </Row>
                        <Space size={"xxs"} />
                        {attachments.map((attachment, index) => (
                            <React.Fragment key={index}>
                                <Column spacing={"200"}>
                                    <Row width={"100%"} widths={["fill", "fit"]}>
                                        <Text>{attachment.fileName}</Text>
                                        <Button
                                            type="icon"
                                            onClick={() => handleDownload(attachment)}
                                        >
                                            <Icon tokens={downloadSmallTokens} color="primary" />
                                        </Button>
                                    </Row>
                                    <HorizontalLine />
                                </Column>
                            </React.Fragment>
                        ))}
                        <Space size={"xxs"} />
                        {allowUpload && (
                            <Row width={"20%"}>
                                <Button type="secondary" size="small" onClick={handleAddAttachment}>
                                    Add Attachment
                                </Button>
                            </Row>
                        )}
                    </Column>
                </Row>
            </Sheet>
            <FileUploadModal
                data={data}
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onFileUpload={handleFileUpload}
            />
        </>
    );
};
